/**
 * CityPicker — debounced type-ahead for hotel destinations.
 *
 * Backs the home-page HotelSearchForm. Calls
 *   GET /api/hotels/cities/autocomplete?q=...&limit=10
 * (backed by our 89k-row tripjack_hotel_cities master).
 *
 * UX:
 *   - "Beautiful" mode: each suggestion row shows a city-name initial-letter
 *     avatar + region subtitle. Heavier than a flat text list but visually
 *     rich — matches the user-confirmed "type-ahead with images" decision.
 *   - 250ms debounce so we don't hammer the BFF on every keystroke.
 *   - First-result auto-highlight + Enter-to-select.
 *   - data-testid prefix: `hotel-citypicker-*`.
 */
import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2, X, Building2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const _CITY_INITIAL_COLOURS = [
  'bg-[#1B375B]', 'bg-[#659A9B]', 'bg-amber-500', 'bg-rose-500',
  'bg-emerald-600', 'bg-indigo-600', 'bg-fuchsia-600', 'bg-cyan-600',
];

const _avatarColour = (name) => {
  if (!name) return _CITY_INITIAL_COLOURS[0];
  const idx = (name.charCodeAt(0) + (name.charCodeAt(1) || 0)) % _CITY_INITIAL_COLOURS.length;
  return _CITY_INITIAL_COLOURS[idx];
};

export const CityPicker = ({ label, selectedCity, onSelect, side = 'left' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  // Outside-click closes the dropdown
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Debounced fetch
  useEffect(() => {
    if (!open) return;
    if (!query || query.trim().length < 1) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/api/hotels/cities/autocomplete?q=${encodeURIComponent(query.trim())}&limit=10`,
        );
        if (res.ok) {
          const data = await res.json();
          setRows(Array.isArray(data) ? data : []);
          setActiveIdx(0);
        } else {
          setRows([]);
        }
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, open]);

  const select = (row) => {
    onSelect(row);
    setOpen(false);
    setQuery('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && rows[activeIdx]) {
      e.preventDefault();
      select(rows[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => {
          setOpen(true);
          setTimeout(() => inputRef.current?.focus(), 30);
        }}
        data-testid="hotel-citypicker-trigger"
        className="w-full text-left group"
      >
        <p className="text-[9px] font-bold text-[#659A9B] uppercase tracking-wider mb-0.5" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {label}
        </p>
        <div className="flex items-start gap-2 mt-0.5">
          <MapPin className="w-4 h-4 text-[#1B375B] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm sm:text-base font-bold text-[#1B375B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedCity?.name || 'Choose destination'}
            </p>
            <p className="text-[10px] sm:text-xs text-[#659A9B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {selectedCity?.fullRegionName || selectedCity?.country || 'Type to search any city'}
            </p>
          </div>
        </div>
      </button>

      {open && (
        <div
          className={`absolute top-full mt-2 z-50 w-[min(92vw,420px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden ${side === 'right' ? 'right-0' : 'left-0'}`}
          data-testid="hotel-citypicker-dropdown"
        >
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Search cities, e.g. Mumbai, Dubai…"
                data-testid="hotel-citypicker-input"
                className="w-full pl-9 pr-9 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm text-[#1B375B] placeholder:text-gray-400 focus:outline-none focus:border-[#659A9B] focus:bg-white transition-colors"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              />
              <MapPin className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-100"
                  data-testid="hotel-citypicker-clear"
                >
                  <X className="w-3.5 h-3.5 text-gray-500" />
                </button>
              )}
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto">
            {loading && (
              <div className="flex items-center justify-center py-6 text-sm text-gray-500" data-testid="hotel-citypicker-loading">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Searching cities…
              </div>
            )}
            {!loading && rows.length === 0 && query && (
              <div className="px-4 py-6 text-center text-sm text-gray-500" data-testid="hotel-citypicker-empty">
                No cities match "{query}". Try a different spelling.
              </div>
            )}
            {!loading && rows.length === 0 && !query && (
              <div className="px-4 py-6 text-center text-xs text-gray-400">
                Start typing — over 89,000 cities supported.
              </div>
            )}
            {!loading && rows.map((row, i) => {
              const isVicinity = (row.type || '').toUpperCase() === 'MULTI_CITY_VICINITY';
              return (
                <button
                  type="button"
                  key={row.cityid}
                  onMouseEnter={() => setActiveIdx(i)}
                  onClick={() => select(row)}
                  data-testid={`hotel-citypicker-row-${row.cityid}`}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${i === activeIdx ? 'bg-[#E7EEEE]' : 'hover:bg-gray-50'}`}
                >
                  <div className={`w-9 h-9 rounded-lg ${_avatarColour(row.name)} flex items-center justify-center text-white font-bold text-sm shrink-0`}>
                    {row.name?.[0] || '?'}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-[#1B375B] truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {row.name}
                      </span>
                      {isVicinity && (
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#659A9B]/10 text-[#659A9B] rounded shrink-0">
                          Metro
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] sm:text-[11px] text-gray-500 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {row.fullRegionName || row.country}
                    </p>
                  </div>
                  <Building2 className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CityPicker;
